import { LogOut, X, Loader2, CheckCircle, Shield, AlertCircle } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

export default function LogoutSection({ navigate }) {
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const handleLogout = () => {
    setLoggingOut(true);
    localStorage.clear();
    setTimeout(() => {
      navigate("/");
    }, 1500);
  };

  return (
    <>
      {/* Logout Button */}
      <div className="p-3 border-t border-gray-700">
        <motion.button
          type="button"
          className="flex items-center justify-center gap-2 w-full py-2.5 px-4
                     bg-gradient-to-r from-blue-600 to-blue-700
                     text-white rounded-lg font-medium text-sm shadow-md
                     hover:shadow-lg hover:from-blue-700 hover:to-blue-800
                     focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900
                     transition-all duration-200 group"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowLogoutModal(true)}
        >
          <LogOut size={16} className="group-hover:translate-x-0.5 transition-transform duration-200" />
          <span>Sign Out</span>
        </motion.button>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {(showLogoutModal || loggingOut) && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {!loggingOut ? (
              <motion.div
                role="dialog"
                aria-modal="true"
                aria-labelledby="logout-title"
                className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-sm border border-gray-700"
                initial={{ scale: 0.95, opacity: 0, y: 10 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 10 }}
                transition={{ type: "spring", stiffness: 380, damping: 40 }}
              >
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-900/40 to-blue-800/40 p-4 border-b border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-600/30 rounded-full flex items-center justify-center">
                      <AlertCircle className="text-blue-400" size={20} />
                    </div>
                    <div className="text-left">
                      <h3 id="logout-title" className="text-lg font-semibold text-white">
                        Confirm Sign Out
                      </h3>
                      <p className="text-xs text-gray-400 mt-1">
                        You will be logged out from this session
                      </p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <div className="bg-blue-900/20 rounded-lg p-3.5 border border-blue-800/50 mb-4">
                    <div className="flex items-start gap-2.5">
                      <Shield size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-blue-200">
                        <p className="font-semibold">Security Notice</p>
                        <p className="mt-0.5 text-blue-300">Your session will be securely closed</p>
                      </div>
                    </div>
                  </div>

                  <div className="text-xs text-gray-400">
                    <p className="font-semibold text-gray-300 mb-3">What happens next:</p>
                    <ul className="space-y-2 ml-2">
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                        <span>Session will be terminated</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                        <span>Redirected to login page</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                        <span>Your data remains secure</span>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-between items-center gap-3 p-4 bg-gray-800/50 border-t border-gray-700">
                  <button
                    onClick={() => setShowLogoutModal(false)}
                    className="flex-1 px-4 py-2.5 rounded-lg text-sm text-gray-300 hover:text-white border border-gray-600 
                             bg-gray-800 hover:bg-gray-700 font-medium transition-all duration-200"
                  >
                    Cancel
                  </button>
                  
                  <button
                    onClick={handleLogout}
                    className="flex-1 px-4 py-2.5 rounded-lg text-sm bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium 
                             hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl
                             transition-all duration-200"
                  >
                    Sign Out
                  </button>
                </div>
              </motion.div>
            ) : (
              // Logging Out Animation
              <motion.div
                className="flex flex-col items-center justify-center bg-gray-900 rounded-xl shadow-2xl w-full max-w-xs p-8 border border-gray-700"
                initial={{ scale: 0.95, opacity: 0, y: 10 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ type: "spring", stiffness: 380, damping: 40 }}
              >
                {/* Animated Container */}
                <motion.div
                  className="relative mb-6"
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  {/* Background Circle */}
                  <motion.div
                    className="absolute inset-0 bg-blue-500/10 rounded-full blur-md"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, duration: 0.4 }}
                  />
                  
                  {/* Checkmark with Animation */}
                  <motion.div
                    className="relative text-blue-400"
                    animate={{
                      scale: [1, 1.1, 1],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    <Loader2 size={48} className="animate-spin" />
                  </motion.div>
                </motion.div>

                {/* Text Content */}
                <motion.div
                  className="text-center"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <h3 className="text-lg font-semibold text-white mb-2">
                    Signing Out
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Please wait while we secure your session...
                  </p>
                </motion.div>

                {/* Progress Dots */}
                <motion.div className="flex gap-1.5 mt-6">
                  {[0, 1, 2].map((index) => (
                    <motion.div
                      key={index}
                      className="w-2 h-2 bg-blue-500 rounded-full"
                      initial={{ scale: 0.6, opacity: 0.5 }}
                      animate={{
                        scale: [0.6, 1, 0.6],
                        opacity: [0.5, 1, 0.5],
                      }}
                      transition={{
                        duration: 1.2,
                        repeat: Infinity,
                        delay: index * 0.15,
                      }}
                    />
                  ))}
                </motion.div>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}